/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * ScanResult
 * ----------
 * Draws one runScan outcome: green to let them in, red with the reason, amber
 * when the organizer has to answer first (late / after the end, which event a
 * shared passcode belongs to, or a pass scanned twice at the entrance), and
 * accent-edged for someone already in: their check-in time and a Check out
 * button. Nothing is saved until a button is tapped; scanning the next pass
 * simply replaces the card. "Not now" only clears the check-out card.
 * The activity comes from the pass's own registration and is shown large, so
 * the volunteer can send the runner to the right start.
 */

import React from 'react';

const TONE = {
  ok: 'ok', stop: 'bad', error: 'bad', confirm: 'warn', choose: 'warn', checkout: 'ask', rescan: 'warn',
};
const ICON = {
  ok: 'bi-check-circle-fill', stop: 'bi-x-octagon-fill', error: 'bi-wifi-off',
  confirm: 'bi-clock-history', choose: 'bi-signpost-split',
  checkout: 'bi-box-arrow-right', rescan: 'bi-arrow-repeat',
};

function ScanResult({ result, busy, eventsById, onConfirm, onChoose, onCheckOut, onDismiss }) {
  if (busy) {
    return (
      <div className="ochk-result is-busy" aria-live="polite">
        <span className="ochk-spinner" aria-hidden="true" />
        <div><p className="ochk-rtitle">Checking the pass…</p></div>
      </div>
    );
  }
  if (!result) {
    return (
      <div className="ochk-result is-idle" aria-live="polite">
        <i className="bi bi-upc-scan" aria-hidden="true" />
        <div>
          <p className="ochk-rtitle">Ready to scan</p>
          <p className="ochk-rdetail">Any pass, for any of your open events and any activity. The pass decides: not in yet checks in, already in offers Check out.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`ochk-result is-${TONE[result.kind]}`} aria-live="polite">
      <i className={`bi ${ICON[result.kind]}`} aria-hidden="true" />
      <div className="ochk-rbody">
        <p className="ochk-rtitle" title={result.title}>{result.title}</p>
        <p className="ochk-rdetail" title={result.detail}>{result.detail}</p>
        {(result.name || result.passcode || result.eventTitle) && (
          <p className="ochk-rwho">
            {result.name && <b className="ochk-name" title={result.name}>{result.name}</b>}
            {result.passcode && <span className="ochk-code">{result.passcode}</span>}
            {result.eventTitle && <span className="ochk-revent" title={result.eventTitle}>{result.eventTitle}</span>}
          </p>
        )}
        {result.kind === 'confirm' && (
          <div className="ochk-ractions">
            <button type="button" className="org-btn ochk-warnbtn" onClick={onConfirm}>Check in anyway</button>
          </div>
        )}
        {result.kind === 'checkout' && (
          <div className="ochk-ractions">
            <button type="button" className="org-btn ochk-outbtn" onClick={onCheckOut}>Check out</button>
            <button type="button" className="org-btn ghost" onClick={onDismiss}>Not now</button>
          </div>
        )}
        {result.kind === 'rescan' && (
          <div className="ochk-ractions">
            <button type="button" className="org-btn ghost" onClick={onCheckOut}>Check out anyway</button>
          </div>
        )}
        {result.kind === 'choose' && (
          <div className="ochk-ractions">
            {result.eventIds.map((id) => (
              <button key={id} type="button" className="org-btn ghost" title={eventsById[id]?.title} onClick={() => onChoose(id)}>
                {eventsById[id]?.title || `Event ${id}`}
              </button>
            ))}
          </div>
        )}
      </div>
      {result.activity && <span className="ochk-activity" title={result.activity}>{result.activity}</span>}
    </div>
  );
}

export default ScanResult;
